// Admin IC Onboarding Modal — Jun 2, 2026
// Opens from the Home page for admin roles. Enter new IC info → creates
// team_roster record → generates signing link → copies to clipboard.

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { icSigningLink, sendIcSigningEmail } from "../../lib/icOnboardingEmail";
import { suggestedLines, type OrgMember } from "../../lib/orgChart";
import { todayET } from "../../lib/dailyCommitments";

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function OnboardICModal({ onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [role, setRole] = useState<string>("restore_manager");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ name: string; link: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const canSubmit = name.trim().length > 0 && email.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      // Generate a unique onboarding token
      const token = crypto.randomUUID();
      const signingLink = icSigningLink(token);

      // Starting lines (Oct 27, 2026): a new RM gets the one active team lead
      // + ASM, a new TL the ASM (standing rules Aug 21 + Sep 15), anyone else
      // the usual boss for their role on the org chart. Before this, RMs
      // added here landed with no team lead or ASM, so no override paid.
      const { data: rosterRows, error: rosterErr } = await supabase
        .from("team_roster")
        .select("id,full_name,role,team_lead_id,asm_id,reports_to_id,is_active")
        .eq("is_active", true);
      if (rosterErr) throw rosterErr;
      const lines = suggestedLines(role, (rosterRows ?? []) as OrgMember[]);

      // Create the team_roster record
      const { error: insertErr } = await supabase
        .from("team_roster")
        .insert({
          full_name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          address: address.trim() || null,
          role,
          is_active: true,
          onboarding_complete: false,
          ic_onboarding_token: token,
          // Eastern date: the UTC date flips to tomorrow after 8 PM.
          hire_date: todayET(),
          team_lead_id: lines.team_lead_id,
          asm_id: lines.asm_id,
          reports_to_id: lines.reports_to_id,
        });

      if (insertErr) {
        if (insertErr.message.includes("duplicate key") || insertErr.message.includes("unique")) {
          throw new Error("A team member with this email already exists.");
        }
        throw insertErr;
      }

      // Fire-and-forget: send signing link via email. Shared builder in
      // lib/icOnboardingEmail.ts — the Team Roster board's add-member flow
      // sends the identical email, so the two can't drift.
      try {
        await sendIcSigningEmail(email.trim().toLowerCase(), name.trim(), signingLink);
      } catch {
        // Email failure doesn't block onboarding — admin can share the link manually
      }

      setSuccess({ name: name.trim(), link: signingLink });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!success) return;
    try {
      await navigator.clipboard.writeText(success.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select a hidden input
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-bardin-ink">Onboard New Contractor</h2>
            <p className="text-xs text-slate-500 mt-0.5">Creates a team roster record and sends them a signing link.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>

        {/* Success state */}
        {success && (
          <div className="p-5 space-y-4">
            <div className="text-center">
              <div className="text-4xl mb-2">✅</div>
              <h3 className="text-lg font-bold text-bardin-ink">{success.name} Added!</h3>
              <p className="text-sm text-slate-500 mt-1">A signing link email has been sent. You can also share the link directly:</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 break-all text-xs text-slate-600 font-mono">
              {success.link}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCopy}
                className="flex-1 py-2.5 rounded-lg bg-bardin-teal text-white text-sm font-semibold hover:bg-bardin-teal/90 transition"
              >
                {copied ? "Copied!" : "Copy Link"}
              </button>
              <button
                onClick={() => { onCreated(); onClose(); }}
                className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <div className="p-5 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-bardin-ink mb-1">
                Full Name <span className="text-bardin-orange">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jordan Franklin"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                autoFocus
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-bardin-ink mb-1">
                Email <span className="text-bardin-orange">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jordan@bardinroofing.com"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold text-bardin-ink mb-1">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(770) 555-1234"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-xs font-semibold text-bardin-ink mb-1">Address</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St, Canton, GA 30114"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-xs font-semibold text-bardin-ink mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent bg-white"
              >
                <option value="restore_manager">Restore Manager</option>
                <option value="team_lead">Team Lead</option>
                <option value="roof_specialist">Roof Specialist</option>
                <option value="drone_pilot">Drone Pilot</option>
                <option value="report_specialist">Report Specialist</option>
                <option value="qc">QC</option>
                <option value="office">Office</option>
                <option value="finance">Finance</option>
                <option value="project_manager">Project Manager</option>
                <option value="roof_team_manager">Roof Team Manager</option>
              </select>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className={`flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition ${
                  canSubmit ? "bg-bardin-orange hover:bg-bardin-orange/90" : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                {submitting ? "Creating..." : "Create & Send Link"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
